// Operation Hub V1 - Gestion des Tickets
// CRUD complet avec filtres et création

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useGovernance } from "../../hooks/useGovernance";
import { 
  Ticket, Plus, Search, Filter, RefreshCw, Loader2,
  AlertTriangle, Clock, CheckCircle, X, ChevronDown,
  User, FolderOpen, Wrench
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { ClientSearchSelect } from "../../components/ui/client-search-select";
import { formatDateFR } from "../../utils/dateFormat";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};




// Ticket Row Component
const TicketRow = ({ ticket, onClick, priorities, statuses }) => {
  const priority = priorities.find(p => p.id === ticket.priorite);
  const status = statuses.find(s => s.id === ticket.status);
  
  return (
    <div
      onClick={onClick}
      className="p-4 bg-zinc-900/50 rounded-lg border border-white/5 hover:border-white/10 transition-colors cursor-pointer"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-zinc-500 font-mono">{ticket.numero}</span>
            <Badge className={priority?.color || ""} variant="outline">
              {ticket.priorite}
            </Badge>
            <Badge className={status?.color || ""}>
              {status?.label || ticket.status.replace('_', ' ')}
            </Badge>
          </div>
          <h3 className="text-white font-medium">{ticket.titre}</h3>
          {ticket.description && (
            <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{ticket.description}</p>
          )}
          <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
            {ticket.client_nom && (
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {ticket.client_nom}
              </span>
            )}
            {ticket.dossier_reference && (
              <span className="flex items-center gap-1">
                <FolderOpen className="w-3 h-3" />
                {ticket.dossier_reference}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDateFR(ticket.created_at)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Create/Edit Dialog
const TicketDialog = ({ open, onClose, ticket, onSave, dossiers, installateurs, ticketTypes, priorities, statuses }) => {
  const [form, setForm] = useState({
    titre: "",
    description: "",
    type: "info",
    priorite: "normale",
    source: "manuel",
    client_id: "",
    dossier_id: "",
    installateur_id: ""
  });
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (ticket) {
      setForm({
        titre: ticket.titre || "",
        description: ticket.description || "",
        type: ticket.type || "info",
        priorite: ticket.priorite || "normale",
        source: ticket.source || "manuel",
        client_id: ticket.client_id || "",
        dossier_id: ticket.dossier_id || "",
        installateur_id: ticket.installateur_id || "",
        status: ticket.status || "ouvert"
      });
    } else {
      setForm({
        titre: "",
        description: "",
        type: "info",
        priorite: "normale",
        source: "manuel",
        client_id: "",
        dossier_id: "",
        installateur_id: ""
      });
    }
  }, [ticket, open]);
  
  const handleSubmit = async () => {
    if (!form.titre) return;
    
    setSaving(true);
    try {
      if (ticket) {
        await axios.patch(
          `${API}/api/operation/tickets/${ticket.id}`,
          form,
          { headers: getAuthHeaders() }
        );
      } else {
        await axios.post(
          `${API}/api/operation/tickets`,
          form,
          { headers: getAuthHeaders() }
        );
      }
      onSave();
      onClose();
    } catch (err) {
      alert("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-white/10 max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-white flex items-center gap-2">
            <Ticket className="w-5 h-5 text-blue-400" />
            {ticket ? `Modifier ${ticket.numero}` : "Nouveau ticket"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">Titre *</Label>
            <Input
              value={form.titre}
              onChange={(e) => setForm({...form, titre: e.target.value})}
              placeholder="Titre du ticket"
              className="bg-zinc-800 border-white/10 text-white"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-zinc-300">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              placeholder="Description détaillée..."
              className="bg-zinc-800 border-white/10 text-white min-h-[80px]"
            />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                <SelectTrigger className="bg-zinc-800 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-white/10">
                  {ticketTypes.map(t => (
                    <SelectItem key={t.id} value={t.id} className="text-white">
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Priorité</Label>
              <Select value={form.priorite} onValueChange={(v) => setForm({...form, priorite: v})}>
                <SelectTrigger className="bg-zinc-800 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-white/10">
                  {priorities.map(p => (
                    <SelectItem key={p.id} value={p.id} className="text-white">
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {ticket && (
            <div className="space-y-2">
              <Label className="text-zinc-300">Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger className="bg-zinc-800 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-white/10">
                  {statuses.map(s => (
                    <SelectItem key={s.id} value={s.id} className="text-white">
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className="space-y-2">
            <Label className="text-zinc-300">Client (Commercial Hub)</Label>
            <ClientSearchSelect 
              value={form.client_id} 
              onChange={(id) => setForm({...form, client_id: id || ""})}
              placeholder="Rechercher un client..."
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-zinc-300">Dossier</Label>
            <Select value={form.dossier_id} onValueChange={(v) => setForm({...form, dossier_id: v})}>
              <SelectTrigger className="bg-zinc-800 border-white/10 text-white">
                <SelectValue placeholder="Lier à un dossier" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-white/10">
                <SelectItem value="none" className="text-zinc-400">Aucun</SelectItem>
                {dossiers.map(d => (
                  <SelectItem key={d.id} value={d.id} className="text-white">
                    {d.référence} - {d.titre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <DialogFooter className="flex-shrink-0 border-t border-white/10 pt-4">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={handleSubmit}
            disabled={!form.titre || saving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {ticket ? "Modifier" : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function TicketsManager() {
  const { items: ticketTypes } = useGovernance("ticket_types");
  const { items: priorities } = useGovernance("ticket_priorities");
  const { items: statuses } = useGovernance("ticket_statuses");

  const [tickets, setTickets] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [installateurs, setInstallateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  const loadData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterStatus) params.append("status", filterStatus);
      if (filterPriority) params.append("priorite", filterPriority);
      
      const [ticketsRes, dossiersRes, installateursRes] = await Promise.all([
        axios.get(`${API}/api/operation/tickets?${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/operation/dossiers`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/operation/installateurs`, { headers: getAuthHeaders() })
      ]);
      
      setTickets(ticketsRes.data);
      setDossiers(dossiersRes.data);
      setInstallateurs(installateursRes.data);
    } catch (err) {
      console.error("Erreur chargement tickets:", err);
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterPriority]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  const handleCreate = () => {
    setSelectedTicket(null);
    setDialogOpen(true);
  };
  
  const handleEdit = (ticket) => {
    setSelectedTicket(ticket);
    setDialogOpen(true);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Ticket className="w-6 h-6 text-blue-400" />
            Tickets
          </h1>
          <p className="text-zinc-400">{tickets.length} ticket(s)</p>
        </div>
        <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau ticket
        </Button>
      </div>
      
      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher..."
              className="pl-10 bg-zinc-800 border-white/10 text-white"
            />
          </div>
        </div>
        
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full max-w-[150px] bg-zinc-800 border-white/10 text-white">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-white/10">
            <SelectItem value="none" className="text-zinc-400">Tous</SelectItem>
            {statuses.map(s => (
              <SelectItem key={s.id} value={s.id} className="text-white">
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-full max-w-[150px] bg-zinc-800 border-white/10 text-white">
            <SelectValue placeholder="Priorité" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-white/10">
            <SelectItem value="none" className="text-zinc-400">Toutes</SelectItem>
            {priorities.map(p => (
              <SelectItem key={p.id} value={p.id} className="text-white">
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Tickets List */}
      {tickets.length === 0 ? (
        <div className="text-center py-12">
          <Ticket className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">Aucun ticket</p>
          <Button onClick={handleCreate} className="mt-4">
            <Plus className="w-4 h-4 mr-2" />
            Créer un ticket
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map(ticket => (
            <TicketRow
              key={ticket.id}
              ticket={ticket}
              onClick={() => handleEdit(ticket)}
              priorities={priorities}
              statuses={statuses}
            />
          ))}
        </div>
      )}
      
      {/* Dialog */}
      <TicketDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        ticket={selectedTicket}
        onSave={loadData}
        dossiers={dossiers}
        installateurs={installateurs}
        ticketTypes={ticketTypes}
        priorities={priorities}
        statuses={statuses}
      />
    </div>
  );
}
